import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  OnInit,
  ViewChild,
  Output,
  Input,
  OnDestroy,
} from "@angular/core";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { TraderGroupLookupService } from "shared/cubiq-services";
import { auditTime, Subject, Subscription } from "rxjs";
import { BfxBaseBlotterContextMenu } from "app/bfx-base-blotter/bfx-base-blotter-contextmenu";
import { NativeDesktopSupport } from "app/bfx-native-desktop/native-desktop-support";

import { LoggerService } from "@bfx/logger";
import { BFXDomHelper } from "app/bfx-common/helpers/bfx-dom-helper";
import { NativeDesktopWindow } from "shared/native-desktop/NativeDesktopWindow";
import { WindowType } from "shared/native-desktop/NativeDesktopWindowOptions";
const logger = LoggerService.for(__dirname);

export class BmarkSpotRiskTraderGroupsMenuItem {
  constructor(
    public id: CubiqTS.Long,
    public title: string,
  ) {}
}

export class SelectedTraderGroupEvent {
  constructor(
    public traderGroupId: CubiqTS.Long,
    public menuIdentifier: string,
  ) {}
}

@Component({
  selector: "bfx-spot-risk-tradergroups-menu",
  templateUrl: "./html/bfx-spot-risk-tradergroups-menu.html",
  standalone: false,
})
export class BmarkSpotRiskTraderGroupsMenuComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild("cm", { read: ElementRef, static: true })
  public vw: ElementRef;

  public position: any = {};
  protected showPostiveAmountSign: boolean = true;
  private menuBoundingClientRect: any = {};
  public isDisplayed: boolean = false;
  public isVisible: boolean = false;
  public openSubject: EventEmitter<void> = new EventEmitter<void>();
  public openingSubject: EventEmitter<void> = new EventEmitter<void>();
  public perfectScrollHeight: number;
  public containerWidth: number = 400;
  public minMenuWidth: number = 400;
  public containerHeight: number = 250;
  private parentMenu: BfxBaseBlotterContextMenu<CubiqTS.OrderDataImpl> = null;
  private readonly containerMinHeight: number = 250;

  public menuItems: Array<BmarkSpotRiskTraderGroupsMenuItem> = [];

  private static _window: NativeDesktopWindow = null;

  private static get window(): NativeDesktopWindow {
    return BmarkSpotRiskTraderGroupsMenuComponent._window;
  }

  private static set window(value: NativeDesktopWindow) {
    logger.out.info(
      `BmarkSpotRiskTraderGroupsMenuComponent.window set (value is  null: ${value == null})`,
    );
    BmarkSpotRiskTraderGroupsMenuComponent._window = value;
  }

  private _parentElement: Element;

  private _subscriptionOpening: Subscription;
  private _subscriptionOpen: Subscription;

  @Input("menuIdentifier")
  public menuIdentifier: string = null;

  @Output("traderGroupSelected")
  public traderGroupSelected: EventEmitter<SelectedTraderGroupEvent> =
    new EventEmitter<SelectedTraderGroupEvent>();

  public control: any = {
    mode: true,
    containerHeight: 100,
    // perfect scrollbar configuration
    scrollbarConfig: {
      suppressScrollX: true,
      wheelSpeed: 1,
      minScrollbarLength: 20,
    },
  };

  public config: any = {
    debug: false,
  };

  private afterViewInitResolve;

  public afterViewInitReady = new Promise<void>((res) => {
    this.afterViewInitResolve = res;
  });

  private static nativeWindowReady: Promise<void> = null;

  private _subscriptionHide: any;

  constructor(protected traderGroupService: TraderGroupLookupService) {
    this._subscriptionOpening = this.openingSubject.subscribe(this.handleOpening.bind(this));
    this._subscriptionOpen = this.openSubject.subscribe(this.handleOpen.bind(this));

    this.initialize();
  }

  protected initialize(): void {
    this.menuItems = this.traderGroupService
      .getAll()
      .filter(
        (traderGroup: CubiqTS.TradingGroupImpl) =>
          traderGroup.isBenchmarkFg &&
          traderGroup.isAutoFilledFg &&
          traderGroup.groupId.toNumber() >= 0,
      )
      .map(
        (traderGroup: CubiqTS.TradingGroupImpl) =>
          new BmarkSpotRiskTraderGroupsMenuItem(traderGroup.groupId, traderGroup.groupCode),
      );

    this.menuItems = this.menuItems.sort(
      (a: BmarkSpotRiskTraderGroupsMenuItem, b: BmarkSpotRiskTraderGroupsMenuItem) =>
        a.title.localeCompare(b.title),
    );

    if (
      NativeDesktopSupport.isNativeDesktopContainer() &&
      BmarkSpotRiskTraderGroupsMenuComponent.nativeWindowReady == null
    ) {
      this.initializeNativeWindow();
    } else {
      BmarkSpotRiskTraderGroupsMenuComponent.nativeWindowReady = Promise.resolve();
    }
  }

  protected initializeNativeWindow(): void {
    BmarkSpotRiskTraderGroupsMenuComponent.nativeWindowReady = new Promise<void>((res) => {
      let id: number = Math.random() * 10000;
      this.position.x = this.position.y = 0;
      if (BmarkSpotRiskTraderGroupsMenuComponent.window == null) {
        NativeDesktopSupport.openWindow(
          "menu-vwap-" + id,
          WindowType.Utility,
          false,
          null,
          ((window: NativeDesktopWindow) => {
            BmarkSpotRiskTraderGroupsMenuComponent.window = window;
            window["beforeClose"] = () => {
              if (window != null) window.hide();
              return false;
            };
            res();
          }).bind(this),
        );
      }
    });
  }

  ngOnInit() {
    //
  }

  ngAfterViewInit() {
    this._parentElement = this.vw.nativeElement.parentElement;
    this.afterViewInitResolve();
  }

  ngOnDestroy(): void {
    if (this._subscriptionOpen != null) {
      this._subscriptionOpen.unsubscribe();
      this._subscriptionOpen = null;
    }

    if (this._subscriptionOpening != null) {
      this._subscriptionOpening.unsubscribe();
      this._subscriptionOpening = null;
    }

    if (this._subscriptionHide != null) {
      this._subscriptionHide.unsubscribe();
      this._subscriptionHide = null;
    }
  }

  public handleOpening($event) {
    this.isDisplayed = true;
    setTimeout(() => {
      this.openSubject.emit();
    }, 4);
  }

  public handleOpen($event) {
    Promise.all([
      this.afterViewInitReady,
      BmarkSpotRiskTraderGroupsMenuComponent.nativeWindowReady,
    ]).then(async () => {
      let menuHeight: number = this.vw.nativeElement.offsetHeight;
      let menuWidth: number = this.vw.nativeElement.offsetWidth;
      if ((!menuHeight || !menuWidth) && !NativeDesktopSupport.isNativeDesktopContainer()) return;

      if (
        NativeDesktopSupport.isNativeDesktopContainer() &&
        BmarkSpotRiskTraderGroupsMenuComponent.window == null
      ) {
        logger.out.warn(
          `${this.constructor.name}.handleOpen() returning early due to missing openfin window`,
        );
        return;
      }

      let x: number = this.menuBoundingClientRect.left + this.menuBoundingClientRect.width;
      let y: number = this.menuBoundingClientRect.top;
      if (y + menuHeight > this.containerHeight) {
        y = this.containerHeight - menuHeight;
      }

      let maxWidth: number = window.innerWidth;

      if (menuWidth + x > maxWidth) {
        x = this.menuBoundingClientRect.left - menuWidth;
      }

      if (NativeDesktopSupport.isNativeDesktopContainer()) {
        const openfinWnd = BmarkSpotRiskTraderGroupsMenuComponent.window;
        let wnd: Window = this._parentElement.ownerDocument.defaultView;
        let width: number = this.vw.nativeElement["offsetWidth"] * NativeDesktopSupport.zoomLevel;
        let height: number = this.vw.nativeElement["offsetHeight"] * NativeDesktopSupport.zoomLevel;
        let { x, y } = await NativeDesktopSupport.getUtilityWindowLocation(
          wnd.screenLeft + wnd.outerWidth,
          wnd.screenTop + this.menuBoundingClientRect.top,
          width,
          height,
        );

        openfinWnd.hide();
        document.body.appendChild(this.vw.nativeElement);

        setTimeout(() => {
          if (openfinWnd == null) {
            logger.out.warn(
              `${this.constructor.name}.handleOpen() returning early due to missing openfin window`,
            );
            return;
          }
          const nativeWnd = openfinWnd.getNativeWindow();
          nativeWnd.document.body.innerHTML = "";
          nativeWnd.document.body.appendChild(this.vw.nativeElement);
          openfinWnd.setBounds(x, y, width, height).then(() => {
            openfinWnd.setParentWindow(NativeDesktopSupport.getWindow(wnd.document.body));
            openfinWnd.showAt(x, y).then(async () => {
              await NativeDesktopSupport.restoreUtility(openfinWnd);
            });
          });
        }, 0);
      } else {
        this.position.x = x;
        this.position.y = y;
      }

      setTimeout(() => {
        this.isVisible = true;
      }, 10);
    });
  }

  public showMenuAt(
    menuBoundingClientRect: any,
    parentMenu: BfxBaseBlotterContextMenu<CubiqTS.OrderDataImpl>,
  ): void {
    logger.out.info(`${this.constructor.name}.showMenuAt(%j)`, menuBoundingClientRect);

    const viewElement = this.vw?.nativeElement;
    const offsets = BFXDomHelper.getParentContainerOffsets(viewElement);

    if (NativeDesktopSupport.isNativeDesktopContainer()) {
      if (menuBoundingClientRect.height < this.containerMinHeight) {
        this.containerHeight = this.containerMinHeight;
      } else {
        this.containerHeight = menuBoundingClientRect.height;
      }
      this.menuBoundingClientRect = menuBoundingClientRect;
    } else {
      let translatedRect = Object.assign({}, menuBoundingClientRect);
      translatedRect = {
        top: menuBoundingClientRect.top,
        left: menuBoundingClientRect.left,
        width: menuBoundingClientRect.width,
        height: menuBoundingClientRect.height,
      };
      translatedRect.top -= offsets.top;
      translatedRect.left -= offsets.left;
      this.menuBoundingClientRect = translatedRect;
    }

    this.parentMenu = parentMenu;
    this.containerHeight = offsets.containerHeight;
    this.containerWidth = offsets.containerWidth;

    this.openingSubject.emit();
  }

  public hideMenu(): boolean {
    if (BmarkSpotRiskTraderGroupsMenuComponent.window != null) {
      BmarkSpotRiskTraderGroupsMenuComponent.window.hide();
    }

    if (this.isVisible) this.isVisible = false;
    if (this.isDisplayed) this.isDisplayed = false;
    return false;
  }

  public onScrollReachEnd(event): void {
    if (event.type === "ps-y-reach-end") {
      this.isDisplayed = true;
    }
  }

  public selectedMenuItem(menuItem: BmarkSpotRiskTraderGroupsMenuItem, event: PointerEvent): void {
    logger.out.info(`${this.constructor.name}.selectedMenuItem(%O %O)`, menuItem, event);
    if (!(event !== null && typeof event === "object" && event.isPrimary)) return;

    this.hideMenu();
    this.parentMenu.hideMenu();

    if (this.traderGroupSelected != null) {
      let item: SelectedTraderGroupEvent = new SelectedTraderGroupEvent(
        menuItem.id,
        this.menuIdentifier,
      );
      this.traderGroupSelected.emit(item);

      logger.out.info(`${this.constructor.name} traderGroupSelected: %O`, item);
    } else {
      logger.out.info(`${this.constructor.name} no traderGroupSelected`);
    }
  }
}
